import { FormInputProps } from '@/@types/form.types';
import { cn } from '@/lib/utils';
import { Controller } from 'react-hook-form';
import InformationCircle from '@/components/icons/InformationCircle';
import { useTranslation } from 'react-i18next';
import MDEditor from '@uiw/react-md-editor';

type Props<T extends {}> = {
    label?: string;
    placeholder?: string;
    optional?: boolean;
} & FormInputProps<T>;

const EMarkdownEditor = <T extends {}>({
    label,
    placeholder = 'Enter remarks',
    optional = false,
    fieldName,
    control,
    className,
    containerClassName,
    disabled,
}: Props<T>) => {
    const { t } = useTranslation();

    return (
        <div data-color-mode="light" className={cn('w-full flex flex-col gap-1.5 select-none', containerClassName)}>
            {label && (
                <h4 className="text-sm font-medium">
                    {label}
                    {optional && <span className="text-popover-600"> (optional)</span>}
                </h4>
            )}
            <Controller
                name={fieldName}
                control={control}
                render={({ field: { value, onChange, onBlur }, fieldState, formState }) => (
                    <>
                        <MDEditor
                            className={cn(
                                'border border-popover-100 rounded-semi-xl text-xs text-popover-800 bg-popover-10 focus:outline-hidden focus:border focus:border-primary-color-1000 placeholder:text-popover-600 disabled:bg-popover-50 transition-colors duration-150',
                                fieldState.invalid ? 'border-error-1000' : '',
                                className,
                            )}
                            value={value ?? ''}
                            onChange={(newValue) =>
                                !(disabled || formState.disabled) && onChange(newValue || null)
                            }
                            onBlur={onBlur}
                            preview="edit"
                            textareaProps={{
                                placeholder,
                            }}
                        />

                        {fieldState.invalid && (
                            <div className="flex items-center gap-1.5">
                                <InformationCircle className="size-4 stroke-error-1000" />
                                <span className="text-xs text-error-1000">
                                    {t(fieldState.error?.message as string)}
                                </span>
                            </div>
                        )}
                    </>
                )}
            />
        </div>
    );
};

export default EMarkdownEditor;
